/**
 * src/types/charts/heatmap.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Heat map chart — types, spacing, animation styles, and defaults.
 */

import type { HexColor, HeatMapValueFormat } from '../core/primitives';
import type { GradientStop, DataPointStyle } from '../core/interfaces.shared';
import { TEXT_STYLE_DEFAULTS } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle, makeInfoBox,
  DEFAULT_BACKGROUND, DEFAULT_LAYOUT, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
  DEFAULT_AXIS, DEFAULT_Y_AXIS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';
import type { SchemaNote } from '../chart.schema';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

export type HeatMapAnimationStyle =
  'cascade' | 'wave' | 'fadeIn' | 'rowByRow' | 'columnByColumn' | 'zoom';

export const HEATMAP_ANIMATION_STYLES: HeatMapAnimationStyle[] = [
  'cascade', 'wave', 'fadeIn', 'rowByRow', 'columnByColumn', 'zoom',
];

// Per-cell animation override is not supported for heatmap.
export const HEATMAP_ELEMENT_PANEL_STYLES: HeatMapAnimationStyle[] = [];


// ─────────────────────────────────────────────────────────────────────────────
// DATA TYPES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A single cell in the heatmap grid.
 * `color` when set bypasses the gradient entirely for this cell.
 */
export interface HeatMapCellValue {
  column:         string;
  value:          number;
  color?:         HexColor;
  styleOverride?: DataPointStyle;
}

/**
 * HeatMapDataPoint — one row of the heatmap grid.
 * `name` must match a rowLabel in HeatMapSpacingConfig.
 * `values` length must equal columnLabels.length.
 */
export interface HeatMapDataPoint {
  id:        string;
  chartType: 'heatmap';
  name:      string;
  values:    HeatMapCellValue[];
  order?:    number;
  visible?:  boolean;
}


// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export interface HeatMapSpacingConfig {
  chartType:           'heatmap';
  rowLabels:           string[];
  columnLabels:        string[];
  colorGradient:       GradientStop[];
  cellWidth:           number;
  cellHeight:          number;
  cellCornerRadius:    number;
  cellBorderWidth:     number;
  cellBorderColor:     HexColor;
  cellBorderOpacity:   number;
  showCellValues?:     boolean;
  valueFormat?:        HeatMapValueFormat;
  headerBgColor?:      HexColor;
  rowHeaderStyles?:    (DataPointStyle | null)[];
  columnHeaderStyles?: (DataPointStyle | null)[];
  legendTitle?:        string;
  legendMinLabel?:     string;
  legendMidLabel?:     string;
  legendMaxLabel?:     string;
  animationSpeed:      number;
  animationStyle?:     string;
}


// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const HEATMAP_DEFAULTS: ChartConfigBase<HeatMapSpacingConfig> = {
  chartType: 'heatmap',
  content: {
    titles:    [makeTitle('Climate Comparison')],
    subtitles: [makeSubtitle('Average Temperature by Season')],
    infoBoxes: [makeInfoBox('Key Insights:\n- New York shows most seasonal variation\n- Singapore maintains consistent heat\n- Moscow experiences extreme cold')],
  },
  animation:  { duration: 6, style: 'wave', textSpeed: 1 },
  background: { ...DEFAULT_BACKGROUND },
  layout:     { ...DEFAULT_LAYOUT },
  axes: {
    x: { ...DEFAULT_AXIS,   showGridLines: false, labelStyle: { ...TEXT_STYLE_DEFAULTS.axisLabel, color: '#FFFFFF', fontSize: 22, fontWeight: '700' } },
    y: { ...DEFAULT_Y_AXIS, showGridLines: false, labelStyle: { ...TEXT_STYLE_DEFAULTS.axisLabel, color: '#FFFFFF', fontSize: 20, fontWeight: '700' } },
  },
  legend:     { ...DEFAULT_LEGEND },
  dataLabels: { ...DEFAULT_DATA_LABELS, format: 'value', style: { ...TEXT_STYLE_DEFAULTS.dataLabel, fontSize: 26, fontWeight: 'bold', color: '#FFFFFF' } },
  spacing: {
    chartType:         'heatmap',
    rowLabels:         ['North America', 'Asia Pacific', 'Europe', 'Latin America', 'Middle East', 'Africa'],
    columnLabels:      ['Q1', 'Q2', 'Q3', 'Q4'],
    colorGradient: [
      { stop: 0.0, color: '#3d85c6' },
      { stop: 0.4, color: '#ffd966' },
      { stop: 0.7, color: '#ff9900' },
      { stop: 1.0, color: '#cc0000' },
    ],
    cellWidth:         205,
    cellHeight:        94,
    cellCornerRadius:  4,
    cellBorderWidth:   8,
    cellBorderColor:   '#ffffff',
    cellBorderOpacity: 30,
    animationSpeed:    1,
    showCellValues:    true,
    headerBgColor:     '#1E293B',
    legendTitle:       'Scale',
    legendMinLabel:    'Low',
    legendMidLabel:    'Medium',
    legendMaxLabel:    'High',
  },
  export: { quality: '1080p', isLooping: true, canvasFormat: '16:9' },
};

// ─────────────────────────────────────────────────────────────────────────────
// SCHEMA NOTES — consumed by chart.schema.ts merger and getChartSchemaSlice()
// ─────────────────────────────────────────────────────────────────────────────

export const HEATMAP_SCHEMA_NOTES: Record<string, SchemaNote> = {

  'heatmap.rowLabels': {
    description: 'Ordered list of row label strings. Defines the number of rows in the matrix. Must match data[].name values exactly.',
    type:    'string[]',
    range:   'Array of strings',
    default: '["North America", "Asia Pacific", "Europe", "Latin America", "Middle East", "Africa"]',
    unit:    'string[]',
    affects: ['row count', 'grid height', 'auto-centring calculation'],
    bounds:  ['Must not be empty.', 'Length must equal number of HeatMapDataPoint entries in data[].'],
    synergizes: ['heatmap.cellHeight'],
    tensions:   ['heatmap.cellHeight × rowLabels.length + margins must not exceed canvas height (1080px)'],
    agentNote:  "rowLabels.length × cellHeight + 270 (title/subtitle/legend) ≤ 1080. Max safe rows ≈ 10 at default cellHeight 94px. Reduce cellHeight for more rows.",
  },

  'heatmap.columnLabels': {
    description: 'Ordered list of column label strings. Defines the number of columns. Each HeatMapDataPoint.values[] entry must have a column field matching one of these labels.',
    type:    'string[]',
    range:   'Array of strings',
    default: '["Q1", "Q2", "Q3", "Q4"]',
    unit:    'string[]',
    affects: ['column count', 'grid width', 'auto-centring calculation'],
    bounds:  ['Must not be empty.', 'Each HeatMapDataPoint.values[] length must equal columnLabels.length.'],
    synergizes: ['heatmap.cellWidth'],
    tensions:   ['heatmap.cellWidth × columnLabels.length + row header width must not exceed 1920px'],
    agentNote:  "Max safe columns ≈ 9 at default cellWidth 205px. Formula: max cellWidth ≈ (1920 - 200) / columnLabels.length.",
  },

  'heatmap.colorGradient': {
    description: 'Array of GradientStop objects defining the colour ramp. Low values → first stop colour. High values → last stop colour. Minimum 2 stops; stops must be in ascending order.',
    type:    'GradientStop[]',
    range:   'Array of { stop: 0.0–1.0, color: HexColor }. Minimum 2 stops. FIELD IS "stop" NOT "position".',
    default: '[{stop:0,"#3d85c6"},{stop:0.4,"#ffd966"},{stop:0.7,"#ff9900"},{stop:1,"#cc0000"}]',
    unit:    'GradientStop[]',
    affects: ['every cell colour', 'gradient scale legend bar colours'],
    bounds:  ['Minimum 2 stops.', 'stop values must be strictly ascending from 0.0 to 1.0.'],
    synergizes: ['heatmap.legendMinLabel / legendMidLabel / legendMaxLabel — labels describe the gradient extremes'],
    tensions:   [],
    agentNote:  "CRITICAL: The field name is 'stop' (not 'position'). Always compute as stop: i/(n-1) for evenly-spaced stops. Never spread old stop objects when changing palette size — length mismatch produces undefined stop values which throw non-finite addColorStop() errors. Always guard: stop: isFinite(s.stop) ? s.stop : i/(n-1). Make sure gradient direction matches data semantics (blue=cold, red=hot is conventional for temperature). Avoid gradients through grey or low-saturation midpoints — they make it hard to distinguish values at a glance.",
  },

  'heatmap.cellWidth': {
    description: 'Width of each grid cell in canvas pixels.',
    type:    'number',
    range:   '20–600',
    default: 205,
    unit:    'canvas pixels',
    affects: ['individual cell width', 'total grid width', 'auto-centring position'],
    bounds:  ['Min 20. Max 600.'],
    synergizes: ['heatmap.columnLabels.length'],
    tensions:   ['total grid width = headerWidth(~180px) + cellWidth × columnLabels.length ≤ 1920'],
    agentNote:  "Formula: max cellWidth ≈ (1920 - 200) / columnLabels.length. Reduce for more columns.",
  },

  'heatmap.cellHeight': {
    description: 'Height of each grid cell in canvas pixels.',
    type:    'number',
    range:   '20–300',
    default: 94,
    unit:    'canvas pixels',
    affects: ['individual cell height', 'total grid height'],
    bounds:  ['Min 20. Max 300.'],
    synergizes: ['heatmap.rowLabels.length'],
    tensions:   ['gridY offset + cellHeight × rowLabels.length must not exceed 1080'],
    agentNote:  "Formula: max cellHeight ≈ (1080 - 270) / rowLabels.length. Default 94px × 6 rows = 564 + 270 = 834 — safe. Reduce to 80 for 8+ rows.",
  },

  'heatmap.cellCornerRadius': {
    description: 'Border radius of each cell in canvas pixels. 0 = sharp corners. Higher = rounded.',
    type:    'number',
    range:   '0–50',
    default: 4,
    unit:    'canvas pixels',
    affects: ['cell corner shape'],
    bounds:  ['Max 50.'],
    synergizes: [],
    tensions:   ['heatmap.cellBorderWidth — heavy borders with large radius look inconsistent'],
    agentNote:  "0–8 = professional. >20 = bubbly/playful. Keep under 10 for broadcast/presentation style.",
  },

  'heatmap.cellBorderWidth': {
    description: 'Width of the gap/border between cells in canvas pixels.',
    type:    'number',
    range:   '0–50',
    default: 8,
    unit:    'canvas pixels',
    affects: ['gap between cells'],
    bounds:  [],
    synergizes: ['heatmap.cellBorderColor', 'heatmap.cellBorderOpacity'],
    tensions:   [],
    agentNote:  "Keep border ≤ 20% of cellWidth for visual balance. At small cellWidth/cellHeight a thick border dominates.",
  },

  'heatmap.cellBorderColor': {
    description: 'Hex colour for the border/gap between cells.',
    type:    'HexColor',
    range:   'any valid hex colour',
    default: '"#ffffff"',
    unit:    'hex',
    affects: ['cell border/gap colour'],
    bounds:  [],
    synergizes: ['heatmap.cellBorderWidth', 'heatmap.cellBorderOpacity'],
    tensions:   [],
    agentNote:  "White (#FFFFFF) at low opacity (20–40%) is the default aesthetic. Dark borders require increasing cellBorderOpacity.",
  },

  'heatmap.cellBorderOpacity': {
    description: 'Opacity of the cell border/gap as a percentage. 0 = invisible. 100 = fully opaque.',
    type:    'number',
    range:   '0–100',
    default: 30,
    unit:    'percentage',
    affects: ['cell border visibility'],
    bounds:  ['Min 0. Max 100.'],
    synergizes: ['heatmap.cellBorderColor', 'heatmap.cellBorderWidth'],
    tensions:   [],
    agentNote:  "Default 30% is intentionally subtle. 0% = cells touch with no visible gap. 100% = prominent grid lines.",
  },

  'heatmap.showCellValues': {
    description: 'Whether to render the numeric value inside each cell.',
    type:    'boolean',
    range:   'true | false',
    default: true,
    unit:    'boolean',
    affects: ['cell value labels'],
    bounds:  [],
    synergizes: ['heatmap.valueFormat', 'dataLabels.style.fontSize'],
    tensions:   ['heatmap.cellHeight — values overflow cell bounds at cellHeight < 40px'],
    agentNote:  "Set false for very small cells or when the colour gradient alone conveys the pattern.",
  },

  'heatmap.valueFormat': {
    description: "Display format for cell values. 'auto' = renderer decides based on data range. 'integer' = rounds to whole number. 'decimal' = one decimal place. 'percentage' = appends %.",
    type:    'HeatMapValueFormat',
    range:   "'auto' | 'percentage' | 'raw' | 'integer' | 'decimal'",
    default: '"auto"',
    unit:    'enum',
    affects: ['cell label text format'],
    bounds:  [],
    synergizes: ['heatmap.showCellValues'],
    tensions:   [],
    agentNote:  "Use 'percentage' for 0–100 percentage data. Use 'integer' for clean whole-number data (temperatures, counts). 'auto' is correct for most cases.",
  },

  'heatmap.headerBgColor': {
    description: 'Background fill colour for all row and column header cells.',
    type:    'HexColor',
    range:   'any valid hex colour',
    default: '"#1E293B"',
    unit:    'hex',
    affects: ['row and column header cell backgrounds'],
    bounds:  [],
    synergizes: ['axes.x.labelStyle.color, axes.y.labelStyle.color — use white text (#FFFFFF) on dark headers'],
    tensions:   ['heatmap.colorGradient — header should be clearly distinct from lightest gradient stop to avoid confusion'],
    agentNote:  "Keep darker than the lightest gradient stop so header cells are visually distinct from low-value data cells. Default dark navy (#1E293B) works for most colour schemes.",
  },

  'heatmap.legendTitle': {
    description: 'Label text shown above the gradient scale bar at the bottom of the heatmap.',
    type:    'string',
    range:   'any string',
    default: '"Scale"',
    unit:    'string',
    affects: ['scale bar title label'],
    bounds:  [],
    synergizes: ['heatmap.legendMinLabel', 'heatmap.legendMidLabel', 'heatmap.legendMaxLabel', 'legend.visible'],
    tensions:   [],
    agentNote:  "Always replace 'Scale' with a meaningful label (e.g. 'Temperature °C', 'Revenue $M'). Only visible when legend.visible = true.",
  },

  'heatmap.legendMinLabel': {
    description: 'Label at the low-value (left) end of the gradient scale bar.',
    type:    'string',
    range:   'any string',
    default: '"Low"',
    unit:    'string',
    affects: ['scale bar min label'],
    bounds:  [],
    synergizes: ['heatmap.legendMaxLabel', 'heatmap.colorGradient'],
    tensions:   [],
    agentNote:  "Must be semantically consistent with gradient direction. If gradient goes blue→red for cold→hot, this must be 'Cold' not 'Hot'.",
  },

  'heatmap.legendMidLabel': {
    description: 'Label at the midpoint of the gradient scale bar.',
    type:    'string',
    range:   'any string',
    default: '"Medium"',
    unit:    'string',
    affects: ['scale bar mid label'],
    bounds:  [],
    synergizes: ['heatmap.legendMinLabel', 'heatmap.legendMaxLabel'],
    tensions:   [],
    agentNote:  "Only meaningful if the gradient has a clearly distinct midpoint colour. For monotone gradients, set to empty string.",
  },

  'heatmap.legendMaxLabel': {
    description: 'Label at the high-value (right) end of the gradient scale bar.',
    type:    'string',
    range:   'any string',
    default: '"High"',
    unit:    'string',
    affects: ['scale bar max label'],
    bounds:  [],
    synergizes: ['heatmap.legendMinLabel', 'heatmap.colorGradient'],
    tensions:   [],
    agentNote:  "Must be semantically consistent with legendMinLabel (e.g. 'Cold'/'Hot', not 'Cold'/'Large').",
  },

  'heatmap.animationSpeed': {
    description: 'Multiplier for how quickly cells are revealed relative to total duration. 0 = all cells appear at once. Higher = slower stagger across cells.',
    type:    'number',
    range:   '0–5',
    default: 1,
    unit:    'multiplier',
    affects: ['cell reveal stagger timing'],
    bounds:  ['0 = no stagger. Max 5.'],
    synergizes: ['animation.duration — longer duration + higher animationSpeed = dramatic cascade'],
    tensions:   ['animation.style — rowByRow and columnByColumn use this directly; cascade and wave have internal timing'],
    agentNote:  "0–2 = normal. >3 = last cells take very long. Only use >3 for very small grids (≤4 rows).",
  },

  'heatmap.animationStyle': {
    description: "Animation pattern for cell reveal. Overrides animation.style for heatmap. Valid values are a subset of HeatMapAnimationStyle.",
    type:    'HeatMapAnimationStyle | undefined',
    range:   "'cascade' | 'wave' | 'fadeIn' | 'rowByRow' | 'columnByColumn' | 'zoom'",
    default: '"wave"  (CHART_DEFAULTS.heatmap.animation.style — renderer falls back to "cascade" only when animation.style is explicitly undefined)',
    unit:    'enum',
    affects: ['heatmap cell reveal pattern'],
    bounds:  ['Must be one of the 6 valid heatmap animation styles. Per-cell animation override is NOT supported.'],
    synergizes: ['heatmap.animationSpeed — rowByRow and columnByColumn use animationSpeed directly'],
    tensions:   ['styleOverride.animationStyle — per-cell animation override is NOT supported for heatmap; this is a grid-level setting only'],
    agentNote:  "Valid heatmap styles: 'wave' (CHART_DEFAULTS — smooth sine ripple), 'cascade' (renderer fallback — diagonal sweep), 'fadeIn' (all at once), 'rowByRow' (top down), 'columnByColumn' (left to right), 'zoom' (scale from centre). Never use bar/line/pie style names here. NOTE: the renderer also reads spacing.animationStyle as a legacy fallback if animation.style is absent from the config — always set animation.style explicitly.",
  },

  'heatmap.rowHeaderStyles': {
    description: 'Per-row header style overrides. Index i overrides the header for rowLabels[i]. null at index i = use default AxesConfig.y.labelStyle for that row. Supports the full DataPointStyle: imageUrl, labelText, fontSize, fontColor, highlight, etc.',
    type:    '(DataPointStyle | null)[]',
    range:   'Array of DataPointStyle | null. Empty [] = all defaults.',
    default: '[]',
    unit:    '(DataPointStyle | null)[]',
    affects: ['individual row header visual appearance'],
    bounds:  ['If non-empty, length must equal rowLabels.length.'],
    synergizes: ['heatmap.rowLabels', 'axes.y.labelStyle (global fallback)'],
    tensions:   [],
    agentNote:  "Leave as [] unless explicitly customising headers. Supported DataPointStyle fields for headers: imageUrl, imageOpacity, imageZoom, imageOffsetX/Y, labelText, fontSize, fontColor, fontWeight, highlight, highlightColor, glowSize. NOT supported: fillColor, sizeMultiplier, animationDelay.",
  },

  'heatmap.columnHeaderStyles': {
    description: 'Per-column header style overrides. Index i overrides columnLabels[i]. null = use default axes.x.labelStyle. Same DataPointStyle fields as rowHeaderStyles.',
    type:    '(DataPointStyle | null)[]',
    range:   'Array of DataPointStyle | null. Empty [] = all defaults.',
    default: '[]',
    unit:    '(DataPointStyle | null)[]',
    affects: ['individual column header visual appearance'],
    bounds:  ['If non-empty, length must equal columnLabels.length.'],
    synergizes: ['heatmap.columnLabels', 'axes.x.labelStyle (global fallback)'],
    tensions:   [],
    agentNote:  "Same rules as rowHeaderStyles. Leave as [] for default text headers. To add flag images to column headers: set imageUrl + imageZoom: 100 + imageOpacity: 0.9.",
  },

};
